import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/api/client';
import type { Subscription } from '@/types';

export function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSubscription() {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.getSubscription();
        setSubscription(response.data);
      } catch (err) {
        console.error('Failed to load subscription:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchSubscription();
  }, [isAuthenticated]);

  const handleUpgradeClick = async (tier: 'free' | 'pro' | 'enterprise') => {
    // If not authenticated, send to signup
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    // If already on this tier, do nothing
    if (subscription?.tier === tier) {
      return;
    }

    // For free tier (downgrade), not implemented yet
    if (tier === 'free') {
      return;
    }

    // For enterprise, open email
    if (tier === 'enterprise') {
      window.location.href = 'mailto:sales@vistora.app';
      return;
    }

    // For pro tier, start Stripe checkout
    if (tier === 'pro') {
      // Prevent Enterprise users from downgrading to Pro
      if (subscription?.tier === 'enterprise') {
        return;
      }

      setUpgrading(true);
      try {
        const response = await api.createCheckout({ tier: 'pro' });
        window.location.href = response.data.checkoutUrl;
      } catch (err) {
        console.error('Failed to create checkout:', err);
        setUpgrading(false);
      }
    }
  };

  const getButtonText = (tier: 'free' | 'pro' | 'enterprise'): string => {
    if (!isAuthenticated) {
      return tier === 'enterprise' ? 'Contact Sales' : 'Get Started';
    }

    if (subscription?.tier === tier) {
      return 'Current Plan';
    }

    if (tier === 'free' && isAuthenticated) {
      // Button is disabled for all authenticated users (can't downgrade)
      return 'Current Plan';
    }

    if (tier === 'pro') {
      return upgrading ? 'Processing...' : 'Upgrade to Pro';
    }

    return 'Contact Sales';
  };

  const isButtonDisabled = (tier: 'free' | 'pro' | 'enterprise'): boolean => {
    // If loading, disable all buttons
    if (loading) {
      return true;
    }

    // If authenticated and on this plan, disable
    if (isAuthenticated && subscription?.tier === tier) {
      return true;
    }

    // If upgrading to pro, disable
    if (tier === 'pro' && upgrading) {
      return true;
    }

    // Free tier disabled for authenticated users (can't downgrade)
    if (tier === 'free' && isAuthenticated) {
      return true;
    }

    // Pro tier disabled for Enterprise users (can't downgrade)
    if (tier === 'pro' && subscription?.tier === 'enterprise') {
      return true;
    }

    return false;
  };

  const getButtonStyles = (tier: 'free' | 'pro' | 'enterprise'): string => {
    const disabled = isButtonDisabled(tier);

    if (disabled) {
      return 'w-full px-6 py-3 rounded-lg font-medium bg-gray-200 text-gray-500 cursor-not-allowed';
    }

    // Pro tier (highlighted)
    if (tier === 'pro') {
      return 'w-full bg-white text-blue-600 border-2 border-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors';
    }

    // Default style for Free and Enterprise
    return 'w-full bg-white text-gray-900 border border-gray-300 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors';
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-4">
        <h1 className="text-4xl font-bold text-center mb-4">Simple, Transparent Pricing</h1>
        <p className="text-center text-gray-600 mb-12">
          Choose the plan that's right for you
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Free Tier */}
          <div className="bg-white p-8 rounded-lg shadow">
            {isAuthenticated && subscription?.tier === 'free' && (
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                  CURRENT PLAN
                </span>
              </div>
            )}
            <h3 className="text-2xl font-bold mb-2">Free</h3>
            <p className="text-4xl font-bold mb-6">
              $0<span className="text-lg text-gray-500">/mo</span>
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Up to 3 SOWs</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Unlimited changes per SOW</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Client access tokens</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Change history</span>
              </li>
            </ul>
            <button
              onClick={() => handleUpgradeClick('free')}
              disabled={isButtonDisabled('free')}
              className={getButtonStyles('free')}
            >
              {getButtonText('free')}
            </button>
          </div>

          {/* Pro Tier */}
          <div className="bg-blue-600 text-white p-8 rounded-lg shadow-lg transform scale-105 relative">
            {isAuthenticated && subscription?.tier === 'pro' && (
              <div className="absolute top-4 right-4">
                <span className="inline-block px-3 py-1 bg-white text-blue-600 text-xs font-semibold rounded-full">
                  CURRENT PLAN
                </span>
              </div>
            )}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <div className="bg-orange-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                EARLY BIRD
              </div>
            </div>
            <div className="bg-blue-700 text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
              MOST POPULAR
            </div>
            <h3 className="text-2xl font-bold mb-2">Pro</h3>
            <div className="mb-6">
              <div className="flex items-baseline gap-2">
                <p className="text-2xl line-through opacity-60">$15</p>
                <p className="text-4xl font-bold">
                  $10<span className="text-lg opacity-75">/mo</span>
                </p>
              </div>
              <p className="text-sm opacity-90 mt-2">
                $5 off per month for 6 months, then $15/mo
              </p>
              <p className="text-xs opacity-75 mt-1">
                Discount applied automatically
              </p>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Unlimited SOWs</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Unlimited changes</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Client access tokens</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Priority support</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">✓</span>
                <span>Export to PDF (coming soon)</span>
              </li>
            </ul>
            <button
              onClick={() => handleUpgradeClick('pro')}
              disabled={isButtonDisabled('pro')}
              className={getButtonStyles('pro')}
            >
              {getButtonText('pro')}
            </button>
          </div>

          {/* Enterprise Tier */}
          <div className="bg-white p-8 rounded-lg shadow">
            {isAuthenticated && subscription?.tier === 'enterprise' && (
              <div className="mb-4">
                <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                  CURRENT PLAN
                </span>
              </div>
            )}
            <h3 className="text-2xl font-bold mb-2">Enterprise</h3>
            <p className="text-4xl font-bold mb-6">Custom</p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Everything in Pro</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Custom SOW limits</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Dedicated support</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>SLA guarantee</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">✓</span>
                <span>Custom integrations</span>
              </li>
            </ul>
            <button
              onClick={() => handleUpgradeClick('enterprise')}
              disabled={isButtonDisabled('enterprise')}
              className={getButtonStyles('enterprise')}
            >
              {getButtonText('enterprise')}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
